// Public Guest Feedback Form — Hospitality Experience Engine (iter165)
// Mobile-first, no login required. Resolves QR token → form, then submits.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { CheckCircle2, AlertTriangle, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CxFeedback() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const [answers, setAnswers] = useState({});
  const [guest, setGuest] = useState({ name: '', phone: '', email: '', room_number: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/cx/public/form?token=${token}`);
        if (!cancelled) setMeta(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'This QR is no longer active.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const setAnswer = (qid, value) => setAnswers((p) => ({ ...p, [qid]: value }));

  // iter176 — Guest identity validation. Required flags come from the
  // tenant settings (default: BOTH mandatory). Phone allows ≥7 digits to
  // accommodate country codes; max 15 per ITU E.164.
  const tenantCfg = meta?.tenant || {};
  const requireName = tenantCfg.require_guest_name !== false;
  const requirePhone = tenantCfg.require_guest_phone !== false;
  const phoneDigits = (guest.phone || '').replace(/\D/g, '');
  const phoneValid = !requirePhone || (phoneDigits.length >= 7 && phoneDigits.length <= 15);
  const nameValid = !requireName || guest.name.trim().length > 0;
  const guestValid = phoneValid && nameValid;

  const submit = async () => {
    if (!meta) return;
    // ── Guest-identity gating (iter176) — must happen BEFORE form-question check.
    if (requireName && !guest.name.trim()) {
      setError('Please share your name so we can follow up if needed.');
      return;
    }
    if (requirePhone && !phoneDigits) {
      setError('Mobile number is required.');
      return;
    }
    if (phoneDigits && (phoneDigits.length < 7 || phoneDigits.length > 15)) {
      setError('Please enter a valid mobile number.');
      return;
    }
    // Validate required questions.
    for (const q of meta.form.questions) {
      const v = answers[q.id];
      const isEmpty = (v === undefined || v === '' || v === null) ||
        (Array.isArray(v) && v.length === 0);
      if (q.required && isEmpty) {
        setError(`Please answer: "${q.label}"`);
        return;
      }
    }
    setSubmitting(true); setError(null);
    try {
      const r = await axios.post(`${API}/cx/public/submit`, {
        qr_token: token,
        answers: Object.entries(answers).map(([qid, v]) => ({ question_id: qid, value: v })),
        guest_name: guest.name.trim() || null,
        guest_phone: guest.phone.trim() || null,
        guest_email: guest.email || null,
        room_number: guest.room_number || null,
      });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFBF8] flex items-center justify-center">
        <div className="animate-pulse text-[#5C6B64] text-[13px] tracking-wide">Loading…</div>
      </div>
    );
  }

  if (error && !meta) {
    return (
      <div className="min-h-screen bg-[#FAFBF8] flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-[#E2E8DF]">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto mb-3" />
            <p className="text-[#1A2421] font-medium">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // iter171 — Branded thank-you screen. Pulls custom title / message from
  // the tenant's CX landing-page settings (with sensible defaults).
  if (result) {
    const t = meta?.tenant || {};
    const showPlatform = t.show_platform_branding === true;
    const primary = t.primary_color || 'var(--color-primary)';
    const [pr, pg, pb] = (() => {
      const m = (primary || '').replace('#', '').match(/^([\da-f]{6})$/i);
      return m ? [
        parseInt(m[1].slice(0, 2), 16),
        parseInt(m[1].slice(2, 4), 16),
        parseInt(m[1].slice(4, 6), 16),
      ] : [20, 61, 48];
    })();
    const tyBg = `linear-gradient(180deg, rgba(${Math.max(0, pr - 20)}, ${Math.max(0, pg - 20)}, ${Math.max(0, pb - 20)}, 1) 0%, rgb(${pr}, ${pg}, ${pb}) 50%, rgba(${pr}, ${pg}, ${pb}, 0.96) 100%)`;
    return (
      <div
        className="min-h-screen flex flex-col text-white"
        style={{ background: tyBg }}
        data-testid="cx-thank-you"
      >
        {/* Property logo header */}
        <div className="px-6 pt-12 pb-6 flex flex-col items-center">
          {t.platform_logo_url && (
            <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-4 mb-5">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${t.platform_logo_url}`}
                alt={t.brand_name || t.platform_name}
                className="h-12 w-auto object-contain"
              />
            </div>
          )}
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#A8C5B5] font-medium mb-1">
            {t.brand_name || t.platform_name}
          </div>
        </div>
        {/* Confirmation card */}
        <div className="flex-1 flex items-center justify-center px-6 pb-12">
          <div className="max-w-md w-full text-center">
            <div className="relative mx-auto w-20 h-20 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center mb-6">
              <div className="absolute inset-0 rounded-full bg-white/5 animate-ping" />
              <CheckCircle2 className="w-11 h-11 text-white relative" strokeWidth={1.5} />
            </div>
            <h1 className="text-[28px] sm:text-[32px] font-semibold mb-3 tracking-tight leading-tight">
              {result.thank_you_title || t.thank_you_title || 'Thank you!'}
            </h1>
            <p className="text-[14.5px] text-[#C8DDD0] leading-relaxed max-w-sm mx-auto">
              {result.thank_you_message || t.thank_you_message}
            </p>
            {result.sentiment === 'positive' && (
              <div className="mt-7 inline-flex items-center gap-1.5 text-[12px] text-white/85 bg-white/10 backdrop-blur-sm border border-white/15 px-3.5 py-1.5 rounded-full">
                <Sparkles className="w-3 h-3" />
                We love hearing this!
              </div>
            )}
          </div>
        </div>
        {/* Footer attribution */}
        {showPlatform && (
          <div className="text-center pb-8 text-[10px] tracking-[0.15em] uppercase text-[#7A9F8C]">
            Powered by {t.platform_name || 'RestroPulse'}
          </div>
        )}
      </div>
    );
  }

  const t = meta?.tenant || {};
  const showPlatform = t.show_platform_branding === true;
  const showBrandName = t.show_brand_name !== false;  // default true
  // iter175 — Theme token resolution (with sensible fallbacks).
  const primary = t.primary_color || 'var(--color-primary)';
  const bgColor = t.background_color || '#FAFBF8';
  const themeMode = t.theme_mode || 'light';
  const isDark = themeMode === 'dark'
    || (themeMode === 'auto' && typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
  // Card style → Tailwind class (one source of truth for guest card chrome).
  const CARD_STYLE_CLASS = {
    elevated: isDark ? 'border-white/10 bg-[#1A2421] shadow-2xl' : 'border-[#E2E8DF] bg-white shadow-md',
    flat:     isDark ? 'border-white/5 bg-[#1A2421]/80'  : 'border-[#EEF1EC] bg-white',
    bordered: isDark ? 'border-white/20 bg-transparent'  : 'border-[#1A2421]/12 bg-white',
    glass:    isDark ? 'border-white/15 bg-white/5 backdrop-blur-md'
                      : 'border-white/70 bg-white/65 backdrop-blur-md shadow-sm',
  };
  const cardClass = CARD_STYLE_CLASS[t.card_style] || CARD_STYLE_CLASS.elevated;
  // Border radius → Tailwind utility lookup.
  const RADIUS_CLASS = { none: 'rounded-none', sm: 'rounded-sm', md: 'rounded-md',
                          lg: 'rounded-xl', xl: 'rounded-2xl' };
  const radiusClass = RADIUS_CLASS[t.border_radius] || 'rounded-xl';
  // Font family → utility class.
  const FONT_CLASS = { sans: 'font-sans', serif: 'font-serif', display: 'font-mono italic tracking-tight' };
  const fontClass = FONT_CLASS[t.font_family] || 'font-sans';
  const inkColor = isDark ? '#F0F2EE' : '#1A2421';
  const subInk = isDark ? '#9AB0A1' : '#5C6B64';
  // Resolved logo URL — needs API prefix for relative paths.
  const resolveAsset = (url) => {
    if (!url) return null;
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };
  const logoSrc = resolveAsset(t.logo_url);
  const heroSrc = resolveAsset(t.hero_image_url);
  // Hex → rgb for gradient stops + alpha helpers.
  const hexToRgb = (h) => {
    const m = (h || '').replace('#', '').match(/^([\da-f]{6})$/i);
    return m ? [
      parseInt(m[1].slice(0, 2), 16),
      parseInt(m[1].slice(2, 4), 16),
      parseInt(m[1].slice(4, 6), 16),
    ] : [20, 61, 48];
  };
  const [pr, pg, pb] = hexToRgb(primary);
  const heroBg = `linear-gradient(135deg, rgba(${pr}, ${pg}, ${pb}, 0.92) 0%, rgba(${pr}, ${pg}, ${pb}, 1) 60%, rgba(${Math.max(0, pr - 20)}, ${Math.max(0, pg - 20)}, ${Math.max(0, pb - 20)}, 1) 100%)`;

  return (
    <div className={`min-h-screen ${fontClass}`}
         style={{ background: bgColor, color: inkColor }}
         data-testid="cx-feedback-page">
      {/* ─── Premium hospitality hero (tenant primary color + optional hero image) ─── */}
      <div className="relative text-white overflow-hidden" style={{ background: heroBg }}>
        {/* Optional hero/banner image — layered behind the gradient with subtle darkening. */}
        {heroSrc && (
          <div className="absolute inset-0">
            <img src={heroSrc} alt="" className="w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(${pr}, ${pg}, ${pb}, 0.45) 0%, rgba(${pr}, ${pg}, ${pb}, 0.92) 100%)` }} />
          </div>
        )}
        {/* Subtle texture overlay for depth (when no hero image). */}
        {!heroSrc && (
          <div className="absolute inset-0 opacity-[0.08]" style={{
            backgroundImage: 'radial-gradient(circle at 20% 20%, white 0.5px, transparent 0.5px), radial-gradient(circle at 80% 60%, white 0.5px, transparent 0.5px)',
            backgroundSize: '40px 40px, 60px 60px',
          }} />
        )}
        <div className="relative max-w-xl mx-auto px-6 pt-9 pb-14">
          {/* Property logo + brand name (auto-inherited from outlet/tenant). */}
          {logoSrc ? (
            <div className="flex items-center gap-3 mb-7">
              <div className="bg-white/12 backdrop-blur-sm border border-white/15 rounded-xl p-2.5">
                <img
                  src={logoSrc}
                  alt={t.brand_name || ''}
                  className="h-8 w-auto object-contain"
                />
              </div>
              {showBrandName && (
                <div>
                  <div className="text-[15px] font-semibold tracking-tight">
                    {t.brand_name}
                  </div>
                </div>
              )}
            </div>
          ) : showBrandName ? (
            <div className="text-[15px] font-semibold tracking-tight mb-7" data-testid="cx-brand-text-fallback">
              {t.brand_name || 'Customer Pulse Index'}
            </div>
          ) : null}
          {/* Welcome banner */}
          <div className="text-[10.5px] tracking-[0.22em] uppercase text-[#A8C5B5] font-medium mb-2">
            {meta.category?.name || 'Customer Pulse Index'} · {meta.area.name}
          </div>
          <h1 className="text-[26px] sm:text-[30px] font-semibold leading-tight tracking-tight mb-2">
            {t.welcome_title || 'Share your experience'}
          </h1>
          <p className="text-[14px] text-[#C8DDD0] leading-relaxed max-w-md">
            {t.welcome_subtitle || 'Share your experience — your pulse shapes our service.'}
          </p>
        </div>
      </div>

      {/* ─── Form body ─── */}
      <div className="max-w-xl mx-auto px-5 sm:px-6 pt-7 pb-28 space-y-5">
        {/* Optional guest identification — fields adapt to tenant requirement flags. */}
        <Card className={`${cardClass} ${radiusClass}`}>
          <CardContent className="p-5 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.14em] font-semibold flex items-center justify-between" style={{ color: subInk }}>
              <span>Your details</span>
              {(requireName || requirePhone) && (
                <span className="normal-case tracking-normal text-[10px] opacity-75">
                  * required
                </span>
              )}
            </div>
            <div>
              <Input placeholder={`Your name${requireName ? ' *' : ''}`} value={guest.name}
                className={`h-11 text-[14px] ${
                  requireName && guest.name.trim().length === 0 && error ? 'border-[#C45A41]' : ''
                }`}
                data-testid="cx-guest-name"
                onChange={(e) => setGuest((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div>
              <Input placeholder={`Mobile number${requirePhone ? ' *' : ''}`} value={guest.phone}
                inputMode="tel" autoComplete="tel"
                className={`h-11 text-[14px] ${
                  requirePhone && !phoneValid && guest.phone ? 'border-[#C45A41]' : ''
                }`}
                data-testid="cx-guest-phone"
                onChange={(e) => setGuest((p) => ({ ...p, phone: e.target.value }))} />
              {requirePhone && guest.phone && !phoneValid && (
                <div className="text-[11px] text-[#C45A41] mt-1" data-testid="cx-phone-error">
                  Mobile number must be 7-15 digits (country code OK).
                </div>
              )}
              {requirePhone && (
                <div className="text-[10.5px] mt-1" style={{ color: subInk }}>
                  We use this only to follow up if there's an issue.
                </div>
              )}
            </div>
            <Input placeholder="Email (optional)" value={guest.email} type="email"
              className="h-11 text-[14px]"
              data-testid="cx-guest-email"
              onChange={(e) => setGuest((p) => ({ ...p, email: e.target.value }))} />
          </CardContent>
        </Card>

        {/* Form description (separate from welcome — form-specific notes) */}
        {meta.form.description && (
          <p className="text-[12.5px] leading-relaxed px-1" style={{ color: subInk }}>
            {meta.form.description}
          </p>
        )}

        {/* Questions */}
        {meta.form.questions.map((q, idx) => (
          <Card key={q.id} className={`${cardClass} ${radiusClass}`} data-testid={`cx-q-${q.id}`}>
            <CardContent className="p-5">
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-[10px] font-medium font-mono mt-0.5 shrink-0" style={{ color: subInk }}>
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <Label className="font-medium text-[14.5px] leading-snug" style={{ color: inkColor }}>
                  {q.label}
                  {q.required && <span className="text-[#C45A41] ml-1">*</span>}
                </Label>
              </div>
              <QuestionInput q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} primary={primary} isDark={isDark} radiusClass={radiusClass} />
            </CardContent>
          </Card>
        ))}

        {error && (
          <div className="text-[12.5px] text-[#C45A41] bg-[#FCE8E2] border border-[#F1BFB2] px-3 py-2.5 rounded-md">
            {error}
          </div>
        )}

        <Button
          onClick={submit}
          disabled={submitting || !guestValid}
          className={`w-full h-12 text-white text-[14px] font-medium shadow-md hover:opacity-90 transition-opacity ${radiusClass} disabled:opacity-50`}
          style={{ background: primary }}
          data-testid="cx-submit-btn"
        >
          {submitting ? 'Submitting…' : 'Share your pulse'}
        </Button>

        {/* Footer attribution */}
        {showPlatform && (
          <div className="pt-3 text-center text-[10px] tracking-[0.15em] uppercase" style={{ color: subInk }}>
            Powered by {t.platform_name || 'RestroPulse'}
          </div>
        )}
        {/* iter180g — legal links on public guest page (required for trust + Google verification reference) */}
        <div className="pt-2 flex items-center justify-center gap-3 text-[10px]" style={{ color: subInk }}>
          <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="hover:opacity-70 transition-opacity" data-testid="cpi-privacy-link">Privacy</a>
          <span className="opacity-40">·</span>
          <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="hover:opacity-70 transition-opacity" data-testid="cpi-terms-link">Terms</a>
        </div>
      </div>
    </div>
  );
}

function QuestionInput({ q, value, onChange, primary = 'var(--color-primary)', isDark = false, radiusClass = 'rounded-md' }) {
  // Theme-aware accent + base styling for every question input variant.
  const selectedStyle = { background: primary, borderColor: primary, color: '#fff' };
  const baseClass = isDark
    ? 'bg-white/5 border-white/10 text-white hover:border-white/40'
    : 'bg-white border-[#E2E8DF] text-[#5C6B64] hover:border-[color:var(--color-primary)]';
  if (q.type === 'nps') {
    return (
      <div className="grid grid-cols-11 gap-1.5" data-testid="cx-nps-grid">
        {Array.from({ length: 11 }, (_, i) => (
          <button
            key={i} type="button"
            onClick={() => onChange(i)}
            className={`h-10 rounded-md text-[13px] font-semibold border transition-all ${
              value === i
                ? 'bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)] shadow-md scale-105'
                : 'bg-white border-[#E2E8DF] text-[#5C6B64] hover:border-[color:var(--color-primary)]'
            }`}
          >{i}</button>
        ))}
      </div>
    );
  }
  if (q.type === 'rating') {
    const min = q.scale_min ?? 1; const max = q.scale_max ?? 5;
    return (
      <div className="flex gap-2">
        {Array.from({ length: max - min + 1 }, (_, i) => {
          const v = min + i;
          return (
            <button key={v} type="button" onClick={() => onChange(v)}
              className={`flex-1 h-12 rounded-md text-[15px] font-semibold border transition-all ${
                value === v
                  ? 'bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)] shadow-md'
                  : 'bg-white border-[#E2E8DF] text-[#5C6B64] hover:border-[color:var(--color-primary)]'
              }`}>★ {v}</button>
          );
        })}
      </div>
    );
  }
  if (q.type === 'emoji') {
    const emojis = ['😡', '😞', '😐', '🙂', '😍'];
    return (
      <div className="flex gap-2 justify-between">
        {emojis.map((e, i) => (
          <button key={i} type="button" onClick={() => onChange(i + 1)}
            className={`flex-1 h-14 rounded-md text-[26px] border transition-all ${
              value === i + 1
                ? 'bg-[#EEF4EF] border-[color:var(--color-primary)] scale-110'
                : 'bg-white border-[#E2E8DF] hover:border-[color:var(--color-primary)]'
            }`}>{e}</button>
        ))}
      </div>
    );
  }
  if (q.type === 'yes_no') {
    return (
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => onChange(true)}
          className={`h-12 rounded-md font-medium border transition-all ${
            value === true
              ? 'bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)]'
              : 'bg-white border-[#E2E8DF] text-[#5C6B64] hover:border-[color:var(--color-primary)]'
          }`}>Yes</button>
        <button type="button" onClick={() => onChange(false)}
          className={`h-12 rounded-md font-medium border transition-all ${
            value === false
              ? 'bg-[#C45A41] text-white border-[#C45A41]'
              : 'bg-white border-[#E2E8DF] text-[#5C6B64] hover:border-[#C45A41]'
          }`}>No</button>
      </div>
    );
  }
  if (q.type === 'multiple_choice') {
    return (
      <div className="space-y-2" data-testid={`cx-q-mcq-${q.id}`}>
        {(q.options || []).map((opt) => (
          <button key={opt} type="button" onClick={() => onChange(opt)}
            data-testid={`cx-q-mcq-opt-${q.id}-${opt}`}
            className={`w-full text-left px-3 py-3 rounded-md text-[13.5px] border transition-all ${
              value === opt
                ? 'bg-[#EEF4EF] border-[color:var(--color-primary)] text-[color:var(--color-primary)] font-medium'
                : 'bg-white border-[#E2E8DF] text-[#5C6B64] hover:border-[color:var(--color-primary)]'
            }`}>{opt}</button>
        ))}
      </div>
    );
  }
  if (q.type === 'multi_select') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      const has = arr.includes(opt);
      onChange(has ? arr.filter((x) => x !== opt) : [...arr, opt]);
    };
    return (
      <div className="space-y-2" data-testid={`cx-q-multi-${q.id}`}>
        {(q.options || []).map((opt) => {
          const checked = arr.includes(opt);
          return (
            <button key={opt} type="button" onClick={() => toggle(opt)}
              data-testid={`cx-q-multi-opt-${q.id}-${opt}`}
              className={`w-full flex items-center gap-3 text-left px-3 py-3 rounded-md text-[13.5px] border transition-all ${
                checked
                  ? 'bg-[#EEF4EF] border-[color:var(--color-primary)] text-[color:var(--color-primary)] font-medium'
                  : 'bg-white border-[#E2E8DF] text-[#5C6B64] hover:border-[color:var(--color-primary)]'
              }`}>
              <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                checked ? 'bg-[color:var(--color-primary)] border-[color:var(--color-primary)]' : 'bg-white border-[#9AAA9F]'
              }`}>
                {checked && (
                  <svg viewBox="0 0 16 16" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="M3 8.5l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span>{opt}</span>
            </button>
          );
        })}
      </div>
    );
  }
  if (q.type === 'short_text') {
    return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Your answer…" />;
  }
  if (q.type === 'long_text') {
    return <Textarea rows={4} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Tell us more…" />;
  }
  return null;
}
